import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Paper, Typography, Chip } from '@mui/material';
import { SmartToy as TeammateIcon } from '@mui/icons-material';
import { WorkflowNode } from '../../../../types/workflow.types';
import { AgentService } from '../../../../services/api/agent';

const TeammateNode: React.FC<NodeProps<WorkflowNode['data']>> = ({ data, selected }) => {
  const config = data?.config || {};
  const [agentName, setAgentName] = useState<string>('');
  const agentService = useMemo(() => new AgentService(), []);

  const fetchAgentById = useCallback(async (agentId: string) => {
    try {
      const fetchedAgent = await agentService.getAgent(agentId);
      setAgentName(fetchedAgent.custom_name || fetchedAgent.name);
    } catch (error) {
      console.error('Failed to fetch agent:', error);
      // Fallback to displaying the ID
      setAgentName(agentId);
    }
  }, [agentService]);

  useEffect(() => {
    if (config?.teammateId) {
      // Check if teammateId is a UUID (new format) or a name (old format)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(config.teammateId);

      if (isUUID) {
        // New format: fetch agent by ID
        fetchAgentById(config.teammateId);
      } else {
        // Old format: use the name directly
        setAgentName(capitalizeAgentName(config.teammateId));
      }
    }
  }, [config?.teammateId, fetchAgentById]);

  const capitalizeAgentName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      style={{
        padding: 16,
        minWidth: 150,
        maxWidth: 250,
        backgroundColor: '#2196F3',
        color: 'white',
        border: selected ? '3px solid #1976d2' : '1px solid #1976D2',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          backgroundColor: '#1565C0',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <TeammateIcon style={{ fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold">
          {data?.title || 'Teammate'}
        </Typography>
      </div>

      {data?.description && (
        <Typography variant="caption" style={{ opacity: 0.9, fontSize: '0.7rem', marginBottom: 8, display: 'block' }}>
          {data?.description}
        </Typography>
      )}

      {config?.teammateId && (
        <Chip
          label={`Teammate: ${agentName || 'Loading...'}`}
          size="small"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontSize: '0.65rem',
            height: 18,
            marginBottom: 4,
          }}
        />
      )}

      {config?.prompt && (
        <Typography variant="caption" style={{ opacity: 0.8, fontSize: '0.65rem', fontStyle: 'italic' }}>
          "{config?.prompt && config.prompt.length > 50 ? config.prompt.substring(0, 50) + '...' : config?.prompt}"
        </Typography>
      )}

      {!config?.teammateId && !config?.prompt && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.65rem' }}>
          Click to configure AI teammate
        </Typography>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          backgroundColor: '#1565C0',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />
    </Paper>
  );
};

export default TeammateNode;
